import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Stack, Typography, CircularProgress, Button, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Order, OrderStatus, orderStatusTranslations } from '@api/Orders/Order';
import OrdersService from '@api/Orders/OrderCtrl';
import { getOrderLineTotal } from '@utils/Order';
import { currencyFormatter } from '@utils/Numbers';

interface TableCurrentOrdersProps {
    tableId?: string;
    tableName?: string;
}

export const getOrderStatusColor = (status: OrderStatus): string => {
    switch (status) {
        case OrderStatus.Paid:
        case OrderStatus.Completed:
        case OrderStatus.Ready:
            return 'success.main'; // Verde

        case OrderStatus.Preparing:
        case OrderStatus.Route:
            return 'info.main'; // Azul

        default:
            return 'text.secondary'; // Gris (Default)
    }
};

export const TableCurrentOrders: React.FC<TableCurrentOrdersProps> = ({ tableId }) => {
    const ordersService = useMemo(() => new OrdersService(), []);
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);

    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        let isActive = true;

        const fetchTableOrders = async () => {
            if (!tableId) {
                setOrders([]);
                return;
            }
            setLoading(true);
            try {
                const foundOrders = await ordersService.getOrdersByTable(tableId);
                if (isActive) {
                    setOrders(foundOrders);
                }
            } catch (error) {
                console.error('Error fetching table orders', error);
            } finally {
                if (isActive) setLoading(false);
            }
        };

        void fetchTableOrders();

        return () => {
            isActive = false;
        };
    }, [tableId]);

    // Si no hay mesa o no hay pedidos (y no está cargando), no renderizamos nada
    if (!tableId || (orders.length === 0 && !loading)) return null;

    // Calcular total acumulado
    const totalAccumulated = orders.reduce((acc, order) => acc + order.total, 0);
    return (
        <Box sx={{ width: '100%', mt: 1 }}>
            {loading ? (
                <Stack alignItems="center" p={1}>
                    <CircularProgress size={20} />
                </Stack>
            ) : (
                <Stack spacing={1}>
                    <Button
                        onClick={() => setIsOpen(!isOpen)}
                        endIcon={isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        size="small"
                        sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            color: 'text.secondary',
                            borderColor: 'lightgrey',
                            border: 1,
                            borderRadius: 1,
                            px: 2
                        }}
                    >
                        <Typography variant="caption" fontWeight="bold">
                            {orders.length} {orders.length === 1 ? 'Pedido vinculado' : 'Pedidos vinculados'}
                        </Typography>
                    </Button>

                    <Collapse in={isOpen}>
                        <Stack spacing={2} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            {orders.map((order, orderIndex) => (
                                <Box key={order.id || orderIndex}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="caption" fontWeight="bold">
                                            #{order.shortId}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: getOrderStatusColor(order.status) }}
                                        >
                                            {orderStatusTranslations[order.status]}
                                        </Typography>
                                    </Stack>

                                    {order.products.map((product, pIndex) => (
                                        <Stack
                                            key={pIndex}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            pl={1}
                                        >
                                            <Typography variant="body2" fontSize="0.75rem" color="text.primary">
                                                {product.quantity}x {product.product.name}
                                            </Typography>
                                            <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                                                {/* getOrderLineTotal (dual): cost crudo × qty duplica la
                                                    cantidad en líneas POS/v2 (cost ya es total de línea) */}
                                                {currencyFormatter(getOrderLineTotal(product))}
                                            </Typography>
                                        </Stack>
                                    ))}

                                    {/* Divider entre pedidos si hay más de uno */}
                                    {orderIndex < orders.length - 1 && (
                                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                    )}
                                </Box>
                            ))}

                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" fontWeight="bold" color="text.secondary">
                                    Total Acumulado:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                    {currencyFormatter(totalAccumulated)}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Collapse>
                </Stack>
            )}
        </Box>
    );
};
